import { useState } from "react";
import { Link } from "react-router-dom";
import type { ProjectDetail } from "@/mocks/admin/projects";
import StatusBadge from "@/pages/projects/components/StatusBadge";
import CallCustomerPopover from "./CallCustomerPopover";
import NewProjectModal from "@/pages/projects/components/NewProjectModal";

type Props = {
  project: ProjectDetail;
  onSendText?: () => void;
};

export default function DetailHeader({ project, onSendText }: Props) {
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-ink-500 mb-3">
        <Link to="/dashboard" className="hover:text-ink-900 cursor-pointer">
          Dashboard
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/projects" className="hover:text-ink-900 cursor-pointer">
          Projects
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-ink-900 font-semibold font-mono">{project.id}</span>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={`w-14 h-14 rounded-lg ${project.customer.avatarColor} text-white font-display text-xl font-bold flex items-center justify-center shrink-0`}
          >
            {project.customer.initials}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-mono text-xs font-bold text-ink-400">{project.id}</span>
              <StatusBadge status={project.status} size="md" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-ink-900 leading-tight">
              {project.customer.name}
            </h1>
            <div className="text-sm text-ink-500 mt-1">{project.moveType}</div>
            <div className="flex items-center gap-4 flex-wrap mt-2 text-xs text-ink-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-ink-400"></i>
                {project.scheduledStart}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-timer-line text-ink-400"></i>
                {project.estimatedHours}h est.
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-route-line text-ink-400"></i>
                {project.distanceMiles} mi
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <CallCustomerPopover
            customerName={project.customer.name}
            phones={[
              { label: "Primary", number: project.customer.phone },
              { label: "Alternate", number: project.customer.altPhone },
            ]}
            onSendText={onSendText}
          />
          <button className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
            <i className="ri-printer-line"></i>
            Print BOL
          </button>
          <button
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            Edit Project
          </button>
        </div>
      </div>

      <NewProjectModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        editProjectId={project.id}
        onCreate={() => {
          setEditOpen(false);
          // Reload so the project detail reflects the updated data
          if (typeof window !== "undefined") {
            window.location.reload();
          }
        }}
      />
    </div>
  );
}